// src/components/styled/CommonStyles.js
import styled from 'styled-components';
import { useState } from 'react';
import { FaPlus, FaSearch, FaTimes } from 'react-icons/fa';

export const Card = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(3)};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const TableContainer = styled(Card)`
  overflow-x: auto;
  margin-top: ${props => props.theme.spacing(3)};
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  
  th {
    background-color: ${props => props.theme.colors.surface};
    color: ${props => props.theme.colors.primary};
    font-weight: ${props => props.theme.typography.fontWeight.bold};
    text-align: left;
    padding: ${props => props.theme.spacing(2)};
    border-bottom: 2px solid ${props => props.theme.colors.primary};
  }

  td {
    padding: ${props => props.theme.spacing(2)};
    border-bottom: 1px solid ${props => props.theme.colors.inputGray}33;
    color: ${props => props.theme.colors.textPrimary};
  }

  tr:nth-child(even) {
    background-color: ${props => props.theme.colors.surface}66;
  }

  tr:hover {
    background-color: ${props => props.theme.colors.primary}11;
  }
`;

export const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  padding: ${props => props.theme.spacing(1)};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${props => props.theme.colors.primary};
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const SearchContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  align-items: center;
`;

export const IconButton = styled(ActionButton)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  border-radius: ${props => props.theme.borderRadius};
  background-color: ${props => props.theme.colors.surface};

  &:hover {
    background-color: ${props => props.theme.colors.primary}22;
  }
`;

export const ActionIconsContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  justify-content: flex-end;

  svg {
    font-size: 1.2rem;
  }
`;

export const StatusBadge = styled.span`
  display: inline-block;
  padding: ${props => props.theme.spacing(0.5)} ${props => props.theme.spacing(2)};
  border-radius: 12px;
  font-size: ${props => props.theme.typography.fontSize.small};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  background-color: ${props => props.color}22;
  color: ${props => props.color};
`;

export const PageHeader = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(3)};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing(1.5)};
  }
`;

export const HeaderContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing(2)};
  align-items: center;
  justify-content: space-between;

  h1 {
    color: ${props => props.theme.colors.primary};
    font-size: 2em;
    margin: 0;
    flex: 1;
    min-width: 200px;

    @media (max-width: 768px) {
      font-size: 2em;
      min-width: unset;
    }
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

export const SearchWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  
  @media (max-width: 768px) {
    ${props => !props.isExpanded && `
      min-width: unset;
      flex: 0;
    `}
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing(1.5)} ${props => props.theme.spacing(4)};
  border: 1px solid ${props => props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.medium};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  @media (max-width: 768px) {
    ${props => !props.isExpanded && `
      width: 0;
      padding: 0;
      border: none;
    `}
  }
`;

export const SearchIcon = styled.button`
  position: absolute;
  left: ${props => props.theme.spacing(1)};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  padding: ${props => props.theme.spacing(0.5)};

  &:hover {
    color: ${props => props.theme.colors.primary};
  }

  @media (max-width: 768px) {
    ${props => !props.isExpanded && `
      position: static;
      transform: none;
    `}
  }
`;

export const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.black};
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(1.5)} ${props => props.theme.spacing(2)};
  cursor: pointer;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing(1)};
    
    span {
      display: none;
    }
  }
`;

// New ResponsiveHeader Component
// src/components/styled/CommonStyles.js
// ... previous styled components remain the same ...

export const ResponsiveHeader = ({ title, onSearch, onAdd }) => {
    const [isSearchExpanded, setSearchExpanded] = useState(false);
    const [searchValue, setSearchValue] = useState('');
  
    const handleSearchClick = () => {
      setSearchExpanded(!isSearchExpanded);
      if (isSearchExpanded) {
        setSearchValue('');
        onSearch(''); // Pass empty string directly
      }
    };
  
    const handleSearchChange = (e) => {
      const newValue = e.target.value;
      setSearchValue(newValue);
      onSearch(newValue); // Pass the new value directly
    };
  
    return (
      <PageHeader>
        <HeaderContent>
          {!isSearchExpanded && <h1>{title}</h1>}
          <HeaderActions>
            <SearchWrapper isExpanded={isSearchExpanded}>
              <SearchInput
                type="text"
                placeholder={isSearchExpanded ? "Search..." : ""}
                value={searchValue}
                onChange={handleSearchChange}
                isExpanded={isSearchExpanded}
              />
              <SearchIcon
                onClick={handleSearchClick}
                isExpanded={isSearchExpanded}
              >
                {isSearchExpanded ? <FaTimes /> : <FaSearch />}
              </SearchIcon>
            </SearchWrapper>
            <AddButton onClick={onAdd}>
              <FaPlus />
              <span>Add New</span>
            </AddButton>
          </HeaderActions>
        </HeaderContent>
      </PageHeader>
    );
  };